import type {
  DealStageName,
  ListingStageName,
} from '@/features/listing-stage/types';
import type {
  APIRequestBody,
  APIRequestQueryParams,
  APIResponse,
} from './base-fetch';
import { get, post, put, remove } from './base-fetch';
import type { ProjectListing } from './projects.api';

export type CreateProjectListingsPayload =
  APIRequestBody<'/api/v4/projects/{projectId}/listings'>;
type CreateProjectListingResponse =
  APIResponse<'/api/v4/projects/{projectId}/listings'>;

export const createProjectListings = async (
  projectId: string,
  data: CreateProjectListingsPayload,
) =>
  await post<CreateProjectListingResponse>(
    `/v4/projects/${projectId}/listings`,
    data,
  );

export type ProjectListingResponse =
  APIResponse<'/api/v4/listings/{projectListingId}'>;

export const getProjectListingById = async (projectListingId: string) => {
  return get<ProjectListingResponse>(`/v4/listings/${projectListingId}`);
};

type UpdateProjectListingStageResponse =
  APIResponse<'/api/v4/listings/{projectListingId}'>;
export type UpdateProjectListingStagePayload =
  APIRequestBody<'/api/v4/listings/{projectListingId}'>;

export const updateProjectListingStage = async (
  projectListingId: string,
  newListingStageId: string,
) => {
  await put<
    UpdateProjectListingStageResponse,
    UpdateProjectListingStagePayload
  >(`/v4/listings/${projectListingId}`, {
    newListingStatusId: newListingStageId,
  });
};

export const deleteProjectListing = async (listing: ProjectListing) => {
  await remove<void>(`/v4/listings/${listing.id}/delete`);
};

export type ListingStage = APIResponse<'/api/v4/listing-statuses'>[number];
type ListingStageParams = APIRequestQueryParams<'/api/v4/listing-statuses'>;

export const fetchListingStages = async (params?: ListingStageParams) =>
  get<ListingStage[]>('/v4/listing-statuses', { params });

export type VoteUseCaseFitPayload =
  APIRequestBody<'/api/v4/listings/{projectListingId}/vote-use-case-fit'>;

export const voteProjectListingUseCaseFit = async (
  projectListingId: string,
  data: VoteUseCaseFitPayload,
) =>
  await post<void>(`/v4/listings/${projectListingId}/vote-use-case-fit`, data);

export type StageCount = {
  stageId: string;
  count: number;
  stageName: ListingStageName | DealStageName;
};

export type ProjectListingsCountParams =
  APIRequestQueryParams<'/api/v4/projects/{projectId}/project-listings/count'>;

export type ProjectListingCountResponse = Omit<
  APIResponse<'/api/v4/projects/{projectId}/project-listings/count'>,
  'stagesCount'
> & {
  stagesCount: StageCount[];
};

export const fetchProjectListingsCount = async (
  projectId: string,
  params?: ProjectListingsCountParams,
) =>
  get<ProjectListingCountResponse>(
    `/v4/projects/${projectId}/project-listings/count`,
    { params },
  );
